import React, { useState } from 'react';
import {
  Box,
  Flex,
  InlineLink,
  OptionType,
  Pane,
  SegmentedControl,
  SelectMenu,
  Text,
  TextArea,
  Tooltip,
  Typography,
} from '@fullstory/components';
import { InspectModeSidebarRelatedSelectorList } from './relatedselectors';
import { ClickData, ClickTypes, clickTypes, clickTypeToIcon } from '../../types';
import { EngagementMapsState, SegmentData } from '../../Components/types';
import { EngagementMapsDetailsCard } from '../../Components/EngagementMapsDetailsCard';
import { EngagementMapsStatus } from '../../Components/EngagementMapsStatus';
import {
  createSearchActions,
  EngagementMapsRankedCard,
  EngagementMapsRankedCardActionRowInfo,
  EngagementMapsRankedCardFactRowProps,
} from '../../Components/EngagementMapsRankedCard';
import { ElementCard } from '../../Components/v2/ElementCard';
import { noop } from '../../Utils';
import { useFlags } from '@fullstory/react-hooks';
import { Flags } from '@fullstory/client-api';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import { generateRoutePath, SETTINGS_PAGES } from '@fullstory/routes';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { fullstorySelectors } from '@fullstory/redux';
import { SegmentRow } from '../../Components/SegmentRow';
import { PageDefinitionRow } from '../../Components/PageDefinitionRow';
import { PagePathRow } from '../../Components/PagePathRow';
import { TimeFrameRow } from '../../Components/TimeFrameRow';
import { ClicksRow } from '../../Components/ClicksRow';
import { UsersRow } from '../../Components/UsersRow';
import { FormattedMessage, useIntl } from 'react-intl';
import { intl } from '@fullstory/intl';
import { NamedElementRecordMatch } from '@fullstory/named-elements';

const { getOrgId } = fullstorySelectors;

export interface InspectModeActions {
  /**
   * An optional callback for adding the inspected selector as a search criteria to the existing search.
   */
  onAddToSearch?: () => void;
  /**
   * An optional callback for using the inspected selector to create a new search criteria.
   */
  onCreateSearch?: () => void;
  /**
   * An optional callback for using the inspected selector to create a named element.
   */
  onNameElement?: (forceNew: boolean) => void;
  /**
   * An optional callback for using the inspected selector to modify recording rules for that element.
   */
  onModifyRecordingRules?: () => void;
  /**
   * An optional callback for using the inspected selector to create a custom element.
   */
  onAddDefinedEvent?: () => void;
  /**
   * An optional callback for using the inspected selector to exclude elements from frustration heuristics.
   */
  onIgnoreInFrustrationSignals?: () => void;
}

export interface InspectModeSidebarProps {
  /**
   * The text of the current selector for the input.
   */
  selectorValue: string;
  /**
   * A callback provided when the user changes the text for the selector input.
   */
  onSelectorChange: (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    selectorType?: string,
  ) => void;
  /**
   * A list of selectors related to the current one.
   */
  relatedSelectors?: Array<string>;
  /**
   * A callback to handle when a related selector is clicked.
   */
  onRelatedSelectorClick?: (index: number) => void;
  /**
   * The current Click type.
   */
  displayClickTypes: boolean;
  /**
   * The current Click type.
   */
  currentClickType: ClickTypes;
  /**
   * A callback provided when the user wishes to change which Click type is selected.
   */
  onClickTypeChange: (option: null | OptionType<ClickTypes>) => void;
  /**
   * The current state of loading the click data. This is used to determine what loading message to display.
   */
  loadingState: EngagementMapsState;
  /**
   * Information about the segment being used to create the results
   */
  segmentData?: SegmentData;
  /**
   * A callback for handling navigating back to the segment definition upon click
   */
  navigateToSegment: () => void;
  /**
   * Information about the click data itself
   */
  clickData?: ClickData;
  /**
   * A list of possible actions that can be taken after inspecting a selector
   */
  inspectModeActions: InspectModeActions;
  /** Raw selector value */
  rawSelector?: string;
  /**
   * A callback for handling when the user chooses between the Optimized and Full selectors
   */
  onSelectorTypeChange?: (type: SelectorTypeId) => void;
  /**
   * An error message potentially generated when attempting to compute a
   * selector.  (Some elements cannot be reliably selected, like SwiftUI
   * elements and JetPack Compose elements in Native Mobile; instead of
   * trying to generate a selector at all for these, we simply complain.)
   */
  selectorComplaint?: string;
  /**
   * An optional link to a KB article to accompany a selectorComplaint error message
   */
  selectorComplaintLink?: string;
  /**
   * If available, offer a selector for the current topmost UIViewController or Fragment
   */
  logicalPageContentRoot?: string;
  /**
   * Callback to handle clicks on the screen selector
   */
  onLogicalPageContentRootSelectorClick?: (selector: string) => void;
}

type SelectorType = { id: string; label: string };
export type SelectorTypeId = 'optimized' | 'full';

const SelectorTypes: SelectorType[] = [
  {
    id: 'optimized',
    label: intl.formatMessage({ defaultMessage: 'Optimized selector', id: 'r+XPX3' }),
  },
  { id: 'full', label: intl.formatMessage({ defaultMessage: 'Full selector', id: 'n5X5UC' }) },
];

/**
 * The Inspect Mode sidebar responsible for displaying information about the loading state of Inspect Mode.
 */
export const InspectModeSidebar: React.FC<InspectModeSidebarProps> = ({
  selectorValue,
  onSelectorChange,
  relatedSelectors = [],
  onRelatedSelectorClick = noop,
  displayClickTypes = true,
  currentClickType,
  onClickTypeChange,
  loadingState,
  segmentData,
  navigateToSegment,
  clickData,
  inspectModeActions,
  rawSelector,
  onSelectorTypeChange,
  selectorComplaint,
  selectorComplaintLink,
  logicalPageContentRoot,
  onLogicalPageContentRootSelectorClick = noop,
}) => {
  const intl = useIntl();
  const [selectorType, setSelectorType] = useState(SelectorTypes[0]!);
  const flags = useFlags() || new Flags();
  const canAccess = flags.hasBit(Flag.PermModifyPageDefinitions);
  const history = useHistory();
  const orgId = useSelector(getOrgId);
  const { onNameElement } = inspectModeActions;

  const changeSelectorType = (type: SelectorType) => {
    setSelectorType(type);
    onSelectorTypeChange?.(type.id as SelectorTypeId);
  };

  function createClickMapsTypeOption(clickType: ClickTypes): OptionType<ClickTypes> {
    return {
      label: intl.formatMessage(
        {
          defaultMessage:
            '{clickType, select, error_clicked {Error Clicks} rage_clicked {Rage Clicks} dead_clicked {Dead Clicks} other {Clicks}}',
          id: 'Mg+4by',
        },
        { clickType },
      ),
      value: clickType,
      key: clickType,
      icon: clickTypeToIcon[clickType],
    };
  }

  function getSelector(): string {
    return clickData && clickData.topSelectorData && clickData.topSelectorData.length > 0
      ? clickData.topSelectorData[0]!.selector
      : selectorType?.id === 'full' && rawSelector
      ? rawSelector
      : selectorValue;
  }

  function getNamedElementRecords(): NamedElementRecordMatch[] {
    return clickData && clickData.topSelectorData && clickData.topSelectorData.length > 0
      ? clickData.topSelectorData[0]!.namedElementRecords
      : [];
  }

  function getFacts(): Array<EngagementMapsRankedCardFactRowProps> {
    return clickData && clickData.topSelectorData && clickData.topSelectorData.length > 0
      ? clickData.topSelectorData[0]!.facts
      : [];
  }

  function createActions(): Array<EngagementMapsRankedCardActionRowInfo> {
    if (!inspectModeActions.onModifyRecordingRules) {
      return [];
    }
    return [
      {
        title: intl.formatMessage({
          defaultMessage: 'Create data capture rule',
          id: 'r+QQlx',
        }),
        action: inspectModeActions.onModifyRecordingRules,
        icon: 'detections',
        canAccess: flags.hasBit(Flag.PermModifyElementBlocks),
      },
    ];
  }

  const clickMapsTypeOptions: Array<OptionType<ClickTypes>> = clickTypes.map(
    (typeValue: ClickTypes) => createClickMapsTypeOption(typeValue),
  );

  const inspectModeRows =
    segmentData && clickData && clickData.topSelectorData.length > 0 ? (
      <>
        <SegmentRow segmentName={segmentData.segment ?? null} onRowClick={navigateToSegment} />
        {segmentData.pageDefinition ? (
          <PageDefinitionRow
            pageDefinition={segmentData.pageDefinition}
            onPageEdit={() => history.push(generateRoutePath(SETTINGS_PAGES, { orgId }))}
            canAccess={canAccess}
          />
        ) : (
          <PagePathRow
            canonicalPagePath={segmentData.canonicalPagePath}
            domain={segmentData.pageDomain}
          />
        )}
        <TimeFrameRow
          timeFrame={segmentData.timeFrame}
          showTimeFrameWarning={segmentData.showTimeFrameWarning}
        />
        <ClicksRow clicks={clickData.clicks} clickType={currentClickType} />
        <UsersRow users={clickData.clickTypeUsers} clickType={currentClickType} />
      </>
    ) : null;

  return (
    <Box data-component="InspectModeSidebar" data-fs="inspect-mode-sidebar">
      <Pane border="standard" backgroundColor="secondary" borderRadius={6}>
        <Box padding={8}>
          <Tooltip
            placement="top"
            tooltipContent={
              <>
                <FormattedMessage
                  defaultMessage="Fullstory indexes Element Attributes and attempts to remove dynamic class names to create more stable selectors."
                  id="FZLOep"
                />{' '}
                <InlineLink
                  href="https://help.fullstory.com/hc/en-us/articles/360052608053-Using-Element-Attributes-in-Search-Page-Analytics"
                  newTab
                  variant="inverse"
                >
                  <FormattedMessage defaultMessage="Learn more." id="hl3R0S" />
                </InlineLink>
              </>
            }
          >
            <Box padding={[0, 0, 16, 0]}>
              <SegmentedControl
                selected={selectorType}
                options={SelectorTypes}
                onChange={changeSelectorType}
                size="x-small"
              />
            </Box>
          </Tooltip>
          <Box>
            <TextArea
              placeholder={intl.formatMessage({
                defaultMessage: '← Click elements or type selectors',
                id: 'FReyNh',
              })}
              id="inspect-mode-sidebar-input"
              value={selectorType?.id === 'full' && rawSelector ? rawSelector : selectorValue}
              onChange={e => onSelectorChange(e, selectorType?.id)}
              size="medium"
              rows={4}
              resizable
              variant="outline"
              copyable="affixed"
              copyLabel={intl.formatMessage({ defaultMessage: 'Copy Selector', id: 'treZbP' })}
              spellCheck={false}
              autoCorrect="off"
              autoCapitalize="off"
            />
          </Box>
          {selectorComplaint && (
            <Box padding={8}>
              <Typography className="errorMessage" variant="heading5" color="red">
                {selectorComplaint}{' '}
                {selectorComplaintLink && (
                  <InlineLink href={selectorComplaintLink} newTab>
                    Learn more.
                  </InlineLink>
                )}
              </Typography>
            </Box>
          )}
          {logicalPageContentRoot && selectorValue !== logicalPageContentRoot && (
            <Box padding={8}>
              <Text appearance="bodyMedium" color="cta">
                <InlineLink
                  onClick={() => {
                    onLogicalPageContentRootSelectorClick(logicalPageContentRoot);
                    // Since the screen selectors are canonicalized, we need to switch over to 'optimized'
                    setSelectorType(SelectorTypes[0]!);
                  }}
                >
                  {intl.formatMessage({
                    defaultMessage: 'Select Entire Screen',
                    id: 'rXR260',
                  })}
                </InlineLink>
              </Text>
            </Box>
          )}
          {displayClickTypes && (
            <Box
              data-fs="inspect-mode-sidebar-click-type-menu"
              data-click-type={currentClickType}
              paddingTop={16}
            >
              <SelectMenu
                size="large"
                variant="outline"
                fullWidth
                onChange={onClickTypeChange}
                options={clickMapsTypeOptions}
                selected={createClickMapsTypeOption(currentClickType)}
              />
            </Box>
          )}
        </Box>
      </Pane>
      {loadingState != 'to_load' && (
        <Box paddingTop={16}>
          <Pane backgroundColor="secondary" border="standard" borderRadius={8}>
            {displayClickTypes && (
              <EngagementMapsStatus
                name={intl.formatMessage({ defaultMessage: 'Inspect Mode', id: 'FvAU7x' })}
                state={loadingState}
                data={
                  <EngagementMapsDetailsCard
                    name={intl.formatMessage({ defaultMessage: 'Inspect Mode', id: 'FvAU7x' })}
                  >
                    {inspectModeRows}
                  </EngagementMapsDetailsCard>
                }
              />
            )}
            {loadingState != 'loading' && (
              <Pane padding={8} borderRadius={8}>
                <EngagementMapsRankedCard
                  selector={getSelector()}
                  namedElementRecords={getNamedElementRecords()}
                  facts={getFacts()}
                  searchActions={createSearchActions(
                    flags,
                    inspectModeActions.onAddToSearch,
                    inspectModeActions.onCreateSearch,
                    inspectModeActions.onAddDefinedEvent,
                  )}
                  recordingActions={createActions()}
                  onClick={noop}
                  expanded
                />
              </Pane>
            )}
          </Pane>
        </Box>
      )}
      {loadingState != 'loading' && rawSelector && onNameElement && (
        <Box paddingTop={16} testId="element-card-inspect-mode">
          <ElementCard
            onClick={isNew => onNameElement(isNew)}
            namedElementRecords={getNamedElementRecords()}
          />
        </Box>
      )}
      {relatedSelectors.length > 0 && (
        <Box paddingTop={16} testId="similar">
          <InspectModeSidebarRelatedSelectorList
            relatedSelectors={relatedSelectors}
            onRelatedSelectorClick={(index: number) => {
              onRelatedSelectorClick(index);
              // Since the related selectors are canonicalized, we need to switch over to 'optimized'
              setSelectorType(SelectorTypes[0]!);
            }}
          />
        </Box>
      )}
      <Box padding={16}>
        <Flex alignItems="center">
          <InlineLink href="https://help.fullstory.com/hc/en-us/articles/360020622494" newTab>
            <Typography color="inherit" variant="body3">
              <FormattedMessage defaultMessage="Learn more about Inspect Mode" id="haRFC9" />
            </Typography>
          </InlineLink>
        </Flex>
      </Box>
    </Box>
  );
};
